/**
 * Live-canvas animation preview for the active scene.
 *
 * This is the one module that mutates the user's live Fabric canvas for
 * display. Its contract: after exit(), every object's transform, caching and
 * interaction flags are exactly what they were at enter(), and nothing
 * mid-animation was ever persisted. It holds that by:
 *   - resolving every frame from a snapshot taken at enter, never from the
 *     object, so deltas cannot compound;
 *   - suppressing editor history and raising the previewLock that every
 *     toJSON() persistence path consults;
 *   - restoring from the snapshot on every exit path (exit, destroy, a
 *     throwing frame), and in DEV asserting the serialized canvas matches.
 *
 * The player has no clock. videoStore's clock decides the time; the editor
 * hook calls renderAt() per tick and exit() when playback stops.
 */

import type { fabric } from 'fabric';
import { ensureCanvasUids } from '@/lib/objectUid';
import { setPreviewActive } from './previewLock';
import { resolveFrameState } from './animationPresets';
import {
  applyLayerState,
  presetContextFor,
  readLayerAnimation,
} from './layerAnimation';
import {
  addToBaseline,
  baselineJson,
  captureBaseline,
  describeBaselineDiff,
  restoreBaseline,
  type BaselineSnapshot,
} from './layerBaseline';

export interface PreviewPlayerDeps {
  canvas: fabric.Canvas;
  /** Flips skipHistoryRef in CanvasEditorWithAutoSave. */
  setHistorySuppressed: (on: boolean) => void;
  /** False while the editor is mid-loadFromJSON; entering then would snapshot stale objects. */
  canEnter: () => boolean;
  artboard: () => { width: number; height: number };
  /** Called once if a frame throws, after the canvas has been restored. */
  onError?: (err: unknown) => void;
}

export interface PreviewPlayer {
  /** Show scene-local `localMs`. Enters preview on first call. */
  renderAt(localMs: number, sceneDurationMs: number): void;
  /** Restore the canvas. Idempotent. */
  exit(): void;
  isActive(): boolean;
  /** exit() + release. Idempotent. */
  destroy(): void;
}

export function createPreviewPlayer(deps: PreviewPlayerDeps): PreviewPlayer {
  const { canvas } = deps;
  let snapshot: BaselineSnapshot | null = null;
  let enterJson: string | null = null;
  /** Inserts/removals mid-session legitimately change the JSON; skip the assertion then. */
  let structureChanged = false;
  let destroyed = false;
  /** Single selection to give back on exit; multi-selections are not restored. */
  let prevSelected: fabric.Object | null = null;

  const onAdded = (opt: fabric.IEvent) => {
    if (!snapshot || !opt.target) return;
    structureChanged = true;
    addToBaseline(snapshot, opt.target);
    opt.target.objectCaching = false;
    opt.target.evented = false;
  };
  const onRemoved = () => {
    structureChanged = true;
  };

  const enter = (): boolean => {
    if (snapshot) return true;
    if (destroyed || !deps.canEnter()) return false;

    // Selection handles would draw at the authored position, not the animated one.
    const active = canvas.getActiveObject();
    prevSelected = active && active.type !== 'activeSelection' ? active : null;
    canvas.discardActiveObject();
    ensureCanvasUids(canvas);
    enterJson = import.meta.env.DEV ? baselineJson(canvas) : null;
    snapshot = captureBaseline(canvas);
    structureChanged = false;

    // No pointer interaction can mutate state mid-playback.
    canvas.selection = false;
    canvas.skipTargetFind = true;
    for (const [obj] of snapshot.entries) {
      obj.evented = false;
      // Fabric caches each object to an offscreen bitmap; animating scale or
      // opacity with caching on gives blurry or frozen frames.
      obj.objectCaching = false;
    }

    deps.setHistorySuppressed(true);
    setPreviewActive(true);
    canvas.on('object:added', onAdded);
    canvas.on('object:removed', onRemoved);
    return true;
  };

  const exit = (): void => {
    const snap = snapshot;
    if (!snap) return;
    snapshot = null;
    canvas.off('object:added', onAdded);
    canvas.off('object:removed', onRemoved);
    restoreBaseline(canvas, snap);
    canvas.requestRenderAll();
    deps.setHistorySuppressed(false);
    setPreviewActive(false);

    if (enterJson !== null && !structureChanged) {
      const exitJson = baselineJson(canvas);
      if (exitJson !== enterJson) {
        console.error(
          '[previewPlayer] restore mismatch — canvas differs from pre-preview state at',
          describeBaselineDiff(enterJson, exitJson)
        );
      }
    }
    enterJson = null;

    const reselect = prevSelected;
    prevSelected = null;
    if (reselect && reselect.selectable !== false && canvas.getObjects().includes(reselect)) {
      canvas.setActiveObject(reselect);
      canvas.requestRenderAll();
    }
  };

  return {
    renderAt(localMs, sceneDurationMs) {
      if (!enter() || !snapshot) return;
      const t = Math.min(Math.max(0, localMs), sceneDurationMs);
      const artboard = deps.artboard();
      try {
        for (const [obj, base] of snapshot.entries) {
          const anim = readLayerAnimation(obj);
          if (!anim) continue;
          applyLayerState(
            obj,
            resolveFrameState({
              base,
              anim,
              sceneLocalMs: t,
              sceneDurationMs,
              ctx: presetContextFor(obj, base, artboard),
            })
          );
        }
        // Coalesced into Fabric's own rAF; renderAll() here would double-render.
        canvas.requestRenderAll();
      } catch (err) {
        exit();
        deps.onError?.(err);
      }
    },
    exit,
    isActive: () => snapshot !== null,
    destroy() {
      if (destroyed) return;
      exit();
      destroyed = true;
    },
  };
}
